import { createSelector } from '@reduxjs/toolkit'
import { filteredGroupsSelector, servicesSlice } from '@/store/servicesSlice'
import { hasConfiguredMaximum } from '@/utils/getReservationConstraintMessage'
import { ReservationValidationError } from '@/utils/reservationValidationMessages'
import { getBoughtServicesTotal } from '@/utils/timeSchedules'
import { cartSlice } from '..'

type MinQtyErr = {
  type: 'minQty'
  minQty: number
}

export type TicketPageError = {
  groupId: number
  serviceId?: number
} & (MinQtyErr | ReservationValidationError)

export const ticketPageErrorsSelector = createSelector(
  [
    cartSlice.selectors.cartReservations,
    filteredGroupsSelector,
    servicesSlice.selectors.data,
  ],
  (cartReservations, groups, servicesData): TicketPageError[] => {
    const errors: TicketPageError[] = []
    const reservation = cartReservations.find(
      (r) => r.spotType === null && r.spotName === null
    )
    if (!reservation || groups.length === 0) {
      return errors
    }

    const boughServicesMap = new Map<number, number>()

    Object.entries(reservation.boughtServices).forEach((entry) => {
      const [key, boughtServices] = entry
      const serviceId = Number(key)

      boughServicesMap.set(serviceId, getBoughtServicesTotal(boughtServices))
    })

    let globalTotal = 0

    groups.forEach((group) => {
      let groupTotal = 0

      group.services.forEach((service) => {
        const boughtAmount = boughServicesMap.get(service.serviceId) ?? 0
        groupTotal += boughtAmount

        if (
          service.minimumQuantity > 0 &&
          boughtAmount < service.minimumQuantity
        ) {
          errors.push({
            groupId: service.serviceGroupId,
            minQty: service.minimumQuantity,
            serviceId: service.serviceId,
            type: 'minQty',
          })
        }
      })

      globalTotal += groupTotal

      if (
        group.minimumReservationQuantity > 0 &&
        groupTotal < group.minimumReservationQuantity
      ) {
        errors.push({
          groupId: group.serviceGroupId,
          groupName: group.name,
          minQty: group.minimumReservationQuantity,
          type: 'groupMinQty',
        })
      }

      if (
        hasConfiguredMaximum(group.maximumReservationQuantity) &&
        groupTotal > group.maximumReservationQuantity
      ) {
        errors.push({
          groupId: group.serviceGroupId,
          groupName: group.name,
          maxQty: group.maximumReservationQuantity,
          type: 'groupMaxQty',
        })
      }
    })

    if (
      servicesData &&
      servicesData.reservationMinimumQuantity > 0 &&
      globalTotal < servicesData.reservationMinimumQuantity
    ) {
      errors.push({
        groupId: 0,
        minQty: servicesData.reservationMinimumQuantity,
        type: 'globalMinQty',
      })
    }

    if (
      servicesData &&
      hasConfiguredMaximum(servicesData.reservationLimitQuantity) &&
      globalTotal > servicesData.reservationLimitQuantity
    ) {
      errors.push({
        groupId: 0,
        maxQty: servicesData.reservationLimitQuantity,
        type: 'globalMaxQty',
      })
    }

    return errors
  }
)

export const ticketPageReservationErrorsSelector = createSelector(
  [ticketPageErrorsSelector],
  (errors): ReservationValidationError[] =>
    errors.filter(
      (error): error is ReservationValidationError & { groupId: number } =>
        error.type === 'groupMinQty' ||
        error.type === 'groupMaxQty' ||
        error.type === 'globalMinQty' ||
        error.type === 'globalMaxQty'
    )
)

export const ticketPageGroupMinErrorsSelector = createSelector(
  [ticketPageErrorsSelector],
  (errors) => errors.filter((error) => error.type === 'groupMinQty')
)

export const ticketPageGlobalMinErrorSelector = createSelector(
  [ticketPageErrorsSelector],
  (errors) =>
    errors.find(
      (error): error is ReservationValidationError & { groupId: number } =>
        error.type === 'globalMinQty'
    ) ?? null
)

export const ticketPageGlobalMaxErrorSelector = createSelector(
  [ticketPageErrorsSelector],
  (errors) =>
    errors.find(
      (error): error is ReservationValidationError & { groupId: number } =>
        error.type === 'globalMaxQty'
    ) ?? null
)
